/**
 * PATH: packages/kanban-host/src/allocation/boardTree.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-TREE-001
 * PURPOSE: Pure I→O helpers that turn a flat list of boards into a
 *          tree, using ` > ` as a path separator embedded inside the
 *          board's own `name`.
 *
 * WHY: The Allocate-to-Kanban submenu had 50+ boards whose names
 *      shared the same leading tokens ("WLG1 - Site Measure",
 *      "WLG2 - Site Measure", …). A flat list was unscannable. The
 *      user asked for indented groupings that expand on hover with
 *      "year first, then so on". Rather than introduce a `parent_id`
 *      on the BE model + a migration, we treat the EXISTING name as
 *      a path:
 *
 *          "2026 > Q1 > Site Measures > WLG1 - Site Measure"
 *                                                ↑
 *                                       leaf displayed in menu
 *
 *      The convention is opt-in. Boards without ` > ` in the name
 *      still appear as top-level leaves — no regression for un-opted
 *      boards.
 *
 * FLOW:
 *   ┌──────────────────┐   ┌─────────────────────┐   ┌──────────────┐
 *   │ AllocateMenuBoard│ → │ buildBoardTree       │ → │ BoardTreeNode│
 *   │   list (flat)    │   │   - split by ` > `   │   │   (root,     │
 *   │                  │   │   - bucket per       │   │    children, │
 *   │                  │   │     segment          │   │    boards)   │
 *   └──────────────────┘   │   - sort + count     │   └──────────────┘
 *                          └─────────────────────┘
 *
 * DEPENDENCIES:
 *   - ./useAllocateToKanbanMenuData (AllocateMenuBoard type only)
 *
 * RELATED:
 *   - apps/newmark/src/features/kanban/allocation/AllocateToKanbanSubMenu.tsx
 *     (renders the tree recursively via ContextMenuSub)
 *   - apps/newmark/src/features/kanban/allocation/boardTree.test.ts
 *     (every rule below has a dedicated test)
 */

import type { AllocateMenuBoard } from './useAllocateToKanbanMenuData'

/** Separator the user types between path segments inside the board
 *  name. We accept any amount of whitespace around the `>` so
 *  "2026>Q1" and "2026 > Q1" produce the same tree. */
const SEPARATOR_REGEX = /\s*>\s*/

export interface BoardTreeNode {
  /** Last path segment, what we render in the submenu row. The root
   *  node has label === ''. */
  label: string
  /** Full path from root using ` > ` joiner; root.path === ''. */
  path: string
  /** Sub-groups whose paths extend this one. Already sorted. */
  children: BoardTreeNode[]
  /** Leaf boards that terminate at this exact path. Already sorted.
   *  When the user's board name has only one segment (no ` > `), the
   *  board lands in `root.boards`. Otherwise the leaf board lands in
   *  the node whose path equals all-but-last segments. */
  boards: AllocateMenuBoard[]
  /** Total leaves in this subtree (boards here + boards in all
   *  descendants). Surfaced in the menu row as "(N)". */
  leafCount: number
}

// ──────────────────────────────────────────────────────────
// Path parsing
// ──────────────────────────────────────────────────────────

/**
 * Split a board name into its path segments.
 *
 *   "2026 > Q1 > Site Measures > WLG1" → ["2026","Q1","Site Measures","WLG1"]
 *   "2026>Q1"                          → ["2026","Q1"]   (no spaces tolerated)
 *   "WLG1 - Site Measure"              → ["WLG1 - Site Measure"]
 *   "  2026 >  >  Q1  "                → ["2026","Q1"]   (empties dropped)
 *   ""                                 → []
 *
 * Returning `[]` for empty input (rather than `[""]`) makes the
 * tree-builder's "single-segment === root leaf" rule unambiguous.
 */
export function splitBoardPath(name: string): string[] {
  if (!name) return []
  return name
    .split(SEPARATOR_REGEX)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

// ──────────────────────────────────────────────────────────
// Tree sorting
// ──────────────────────────────────────────────────────────

/**
 * Decide how to order two sibling tree-node labels.
 *
 * Rules (deliberately small so the behaviour is predictable):
 *
 *   1. If BOTH labels parse as pure integers → DESCENDING numeric.
 *      Captures the "year first" intent ("2026" before "2025" before
 *      "2024"). Also gives an intuitive ordering for any other
 *      integer-only label (e.g. "10" before "9" if someone numbers
 *      their phases) — operator can override by prefixing letters.
 *   2. Otherwise → ASCENDING `localeCompare`. This naturally orders
 *      "Q1" < "Q2" < "Q3" < "Q4" and is friendly to non-ASCII names.
 *
 * Exported so tests can pin the rule without going through the whole
 * buildBoardTree() pipeline.
 */
export function compareTreeSegments(a: string, b: string): number {
  const ai = Number.parseInt(a, 10)
  const bi = Number.parseInt(b, 10)
  const aIsPureInt = Number.isFinite(ai) && String(ai) === a
  const bIsPureInt = Number.isFinite(bi) && String(bi) === b
  if (aIsPureInt && bIsPureInt) return bi - ai
  return a.localeCompare(b)
}

/**
 * Leaf-board comparator inside a single group. Mirrors the rail's
 * order — `display_order` ASC, ties by `name.localeCompare` — so the
 * operator's mental model of "where in the rail does this board
 * sit" carries into the submenu.
 *
 * IMPORTANT: we sort by the BOARD's original display name, not by
 * the post-rename last-segment label, so a "2026 > Q1 > a" and
 * "2026 > Q1 > b" still sort by what the BE assigned even when they
 * happen to start with the same characters.
 */
export function compareLeafBoards(a: AllocateMenuBoard, b: AllocateMenuBoard): number {
  // AllocateMenuBoard exposes the raw Board on `.board`; reach
  // through to display_order so behaviour matches the rail.
  const ao = typeof a.board.display_order === 'number' ? a.board.display_order : Number.MAX_SAFE_INTEGER
  const bo = typeof b.board.display_order === 'number' ? b.board.display_order : Number.MAX_SAFE_INTEGER
  if (ao !== bo) return ao - bo
  return a.name.localeCompare(b.name)
}

// ──────────────────────────────────────────────────────────
// Tree construction
// ──────────────────────────────────────────────────────────

function makeNode(label: string, path: string): BoardTreeNode {
  return { label, path, children: [], boards: [], leafCount: 0 }
}

/**
 * Build the tree.
 *
 * Algorithm:
 *   1. For each input board, split its name into segments.
 *   2. Single-segment (or no-separator) boards land in `root.boards`
 *      with their original full name preserved. These appear as
 *      flat top-level leaves in the menu — back-compat for un-opted
 *      boards.
 *   3. Multi-segment boards descend through `children`, creating
 *      group nodes by their first N-1 segments. The Nth segment
 *      becomes the leaf's display name (the parent submenus carry
 *      the rest of the context, so the row reads as just "WLG1"
 *      rather than the full "2026 > Q1 > Site Measure > WLG1").
 *   4. After insertion, sort children with `compareTreeSegments`
 *      (year DESC, alpha ASC) and leaves with `compareLeafBoards`
 *      (display_order). Counts roll up bottom-up.
 *
 * Returned root is always defined (label: '', path: ''). Callers
 * iterate `root.children` for groups and `root.boards` for the
 * un-grouped tail.
 */
export function buildBoardTree(boards: AllocateMenuBoard[]): BoardTreeNode {
  const root = makeNode('', '')

  for (const original of boards) {
    const segments = splitBoardPath(original.name)

    // Zero-segment: a board named "" — drop it (defensive; the
    // backend doesn't allow blank names but we don't trust that).
    if (segments.length === 0) continue

    // Single-segment: top-level leaf with its name unchanged.
    if (segments.length === 1) {
      root.boards.push(original)
      continue
    }

    // Multi-segment: walk/create group nodes, then push a renamed
    // leaf at the deepest group.
    let node = root
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i]!
      const path = segments.slice(0, i + 1).join(' > ')
      let child = node.children.find((c) => c.label === segment)
      if (!child) {
        child = makeNode(segment, path)
        node.children.push(child)
      }
      node = child
    }
    const leafSegment = segments[segments.length - 1]!
    // Override `name` to the trailing segment so the submenu row
    // doesn't repeat the path the user already navigated through.
    // The rest of `original` (id, swatch, lanes, raw board) is kept
    // intact so allocations + theming still work.
    node.boards.push({ ...original, name: leafSegment })
  }

  sortTree(root)
  return root
}

/** In-place recursive sort + leafCount tally. Pure with respect to
 *  the OUTSIDE world (no globals touched); only mutates the tree we
 *  just built, which is otherwise unobservable. */
function sortTree(node: BoardTreeNode): number {
  node.children.sort((a, b) => compareTreeSegments(a.label, b.label))
  node.boards.sort(compareLeafBoards)
  let count = node.boards.length
  for (const child of node.children) {
    count += sortTree(child)
  }
  node.leafCount = count
  return count
}

// ──────────────────────────────────────────────────────────
// Convenience predicates
// ──────────────────────────────────────────────────────────

/** True when the node has no children and no boards. Used by the
 *  consumer to decide whether to render the "All boards" header
 *  (we'd hide it if the tree is empty AND the un-grouped bucket
 *  also has nothing). */
export function isEmptyTree(node: BoardTreeNode): boolean {
  return node.children.length === 0 && node.boards.length === 0
}
